import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import MarginIcon from '@mui/icons-material/Margin'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ChecklistIcon from '@mui/icons-material/Checklist'
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark'
import HandshakeIcon from '@mui/icons-material/Handshake'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import { type ReactNode } from 'react'
import { FundsFillIcon } from '../../../../assets/svgs/FundsFillIcon'
import { type DashboardCount, type DashboardProgram, type ProgramBreakdownRow } from '../../../../models/peerToPeer/Dashboard'
import { type TileSpec } from './StatTile'
import { StatusPill } from './StatusPill'
import { compareGrades, formatGrades, gradeLabel } from './gradeLabels'

export type Goal = 'Increase & Maintain' | 'Expand' | 'Enhance'
export const GOALS: Goal[] = ['Increase & Maintain', 'Expand', 'Enhance']
const GOAL_ICON_SX = { fontSize: 24, color: 'var(--purple-500)' } as const
export const GOAL_ICON: Record<Goal, JSX.Element> = {
  'Increase & Maintain': <FundsFillIcon sx={GOAL_ICON_SX} />,
  Expand: <ZoomOutMapIcon sx={GOAL_ICON_SX} />,
  Enhance: <AutoAwesomeIcon sx={GOAL_ICON_SX} />
}

export interface ColSpec {
  label: string
  key: keyof ProgramBreakdownRow
  width?: number
  render?: (row: ProgramBreakdownRow) => ReactNode
}

export interface ChartSpec {
  title: string
  data: DashboardCount[]
  formatKey: (key: string) => string
}

/** Increase & Maintain stacks its two tiles beside the chart; the others run a full-width tile row. */
export type GoalLayout = 'tilesBesideChart' | 'tilesAboveChart'

export interface GoalView {
  layout: GoalLayout
  tiles: TileSpec[]
  columns: ColSpec[]
  chart: ChartSpec | null
}

const TILE_ICON_SX = { fontSize: 75 } as const
const BLUE = { color: 'var(--blue-500)', panel: 'var(--blue-50)' }
const PURPLE = { color: 'var(--purple-500)', panel: 'var(--purple-50)' }
const TEAL = { color: 'var(--light-blue-500)', panel: 'var(--light-blue-50)' }

const NAME_COLUMN_WIDTH = 200
const buildingColumn: ColSpec = { label: 'Building', key: 'building', width: NAME_COLUMN_WIDTH }
const isdColumn: ColSpec = { label: 'ISD', key: 'isd', width: NAME_COLUMN_WIDTH }
const districtColumn: ColSpec = { label: 'District', key: 'district', width: NAME_COLUMN_WIDTH }

const goalViews: Record<Goal, (program: DashboardProgram) => GoalView> = {
  'Increase & Maintain': program => ({
    layout: 'tilesBesideChart',
    tiles: [
      { label: 'Total Existing Programs', value: program.totalExistingPrograms, icon: <MarginIcon sx={TILE_ICON_SX} />, ...BLUE },
      { label: 'Average Years Existed', value: program.averageYearsExisted ?? 'N/A', icon: <CalendarMonthIcon sx={TILE_ICON_SX} />, ...PURPLE }
    ],
    columns: [buildingColumn, { label: 'RCN', key: 'rcn', width: NAME_COLUMN_WIDTH }, isdColumn, districtColumn, { label: 'Years', key: 'years' }],
    chart: { title: 'Years Programs Have Existed', data: program.yearsActive, formatKey: key => key }
  }),
  Expand: program => ({
    layout: 'tilesAboveChart',
    tiles: [
      { label: 'Total Peer Partners', value: program.statTiles.totalPeerPartners, icon: <HandshakeIcon sx={TILE_ICON_SX} />, ...BLUE },
      { label: 'Students with Autism', value: program.statTiles.studentsWithAutism, icon: <AccountCircleIcon sx={TILE_ICON_SX} />, ...PURPLE },
      { label: 'Students with IEP', subtitle: 'Not Autism', value: program.statTiles.studentsWithIepNotAutism, icon: <PlaylistAddCheckIcon sx={TILE_ICON_SX} />, ...TEAL }
    ],
    columns: [
      buildingColumn, isdColumn,
      { label: 'Peers', key: 'peers', width: 84 },
      { label: 'Autism', key: 'autism', width: 84 },
      { label: 'IEP', key: 'iep', width: 68 },
      { label: 'Grades', key: 'grades', render: row => formatGrades(row.grades) }
    ],
    chart: {
      title: 'Grade Levels Served',
      data: [...program.gradeLevelsServed].sort((a, b) => compareGrades(a.key, b.key)),
      formatKey: gradeLabel
    }
  }),
  Enhance: program => ({
    layout: 'tilesAboveChart',
    tiles: [
      { label: 'Fidelity Checklists Completed', value: program.statTiles.fidelityChecklistsCompleted, icon: <ChecklistIcon sx={TILE_ICON_SX} />, ...BLUE },
      { label: 'Programs Offered As Elective Credits', value: program.statTiles.programsOfferedAsElectiveCredit, icon: <CollectionsBookmarkIcon sx={TILE_ICON_SX} />, ...PURPLE }
    ],
    columns: [
      { label: 'Program', key: 'programName', width: NAME_COLUMN_WIDTH }, isdColumn, districtColumn,
      { label: 'Fidelity Checklist', key: 'fidelityChecklist', width: 160, render: row => <StatusPill value={row.fidelityChecklist} /> },
      { label: 'Elective Credit', key: 'electiveCredit', render: row => <StatusPill value={row.electiveCredit} /> }
    ],
    chart: null
  })
}

export const goalViewFor = (goal: Goal, program: DashboardProgram): GoalView => goalViews[goal](program)
